import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { Asset, Cotizacion } from '../../Models/cotizacion';
import { CoveragesGroup, Product } from '../../Models/planes';
import { MembershipTokenResponse } from '../../shared/interfaces/membership.interface';

import { CotizacionService } from '../../services/Cotizacion/cotizacion.service';
import { DataLayerService } from '../../services/DataLayer/data-layer.service';
import { UtilsService } from '../../services/utils.service';
import { MembershipService } from '../../shared/services/membership.service';

// Constantes y Helpers
import { getSessionId } from '../../helpers/storageUtils';
import { PAGO_APROBADO_NO_AP, PAGO_RECHAZADO, REDIRECT } from '../../routes.constans'; // Asumiendo que tienes PAGO_RECHAZADO

// Componentes y Módulos
import { CommonModule } from '@angular/common';
import { PipesModule } from '../../pipes/pipes.module';
import { MobilityLoaderComponent } from '../../shared/components/mobility-loader/mobility-loader.component';
import { SummaryPlanCardComponent } from '../../shared/components/summary-plan-card/summary-plan-card.component';
import { BannerAppComponent } from '../banner-app/banner-app.component';
import { FooterAppComponent } from '../footer-app/footer-app.component';

@Component({
  selector: 'app-procesando-pago',
  standalone: true,
  imports: [
    CommonModule,
    PipesModule,
    BannerAppComponent,
    FooterAppComponent,
    SummaryPlanCardComponent,
    MobilityLoaderComponent,
  ],
  templateUrl: './procesando-pago.component.html',
  styleUrls: ['./procesando-pago.component.scss'],
})
export class ProcesandoPagoComponent implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cotizacionService = inject(CotizacionService);
  private readonly dataLayerService = inject(DataLayerService);
  private readonly membershipService = inject(MembershipService);
  private readonly utils = inject(UtilsService);

  readonly isLoading = signal(true);
  private readonly sessionId = signal(getSessionId().toString());
  private readonly codAsesor = signal('');
  readonly wantsRecurringPayment = signal(false);

  readonly cotizacionData = signal<Cotizacion | null>(null);

  readonly paymentFrequencyText = computed(() => {
    const freq = this.cotizacionData()?.data.plan.paymentFrecuency;
    if (freq === 'Annually') return 'Anual';
    if (freq === 'Monthly') return 'Mensual';
    return freq ?? 'No definido';
  });

  /**
   * ADAPTADOR: Transforma los datos de `cotizacionData` al formato `Product`
   * que espera el componente hijo <sura-summary-plan-card>.
   */
  readonly pagoPlanForChild = computed<Product | undefined>(() => {
    const plan = this.cotizacionData()?.data.plan;
    if (!plan) return undefined;

    return {
      code: plan.selected,
      name: plan.selected,
      coveragesGroup: plan.coveragesGroup,
      sumInsured: plan.sumInsured,
      priceAnual: plan.price.full,
      priceMensual: plan.price.price_monthly,
    } as Product;
  });

  readonly coveragesIncluded = computed<CoveragesGroup[]>(() => {
    return this.pagoPlanForChild()?.coveragesGroup?.filter((c) => c.include) ?? [];
  });

  readonly cuidaCirculacion = computed(() => {
    const zona = this.cotizacionData()?.data.plan.zonaCirculacion ?? '';
    if (!zona) return '';
    return zona.charAt(0).toUpperCase() + zona.slice(1).toLowerCase();
  });

  readonly valorMes = computed(() => this.pagoPlanForChild()?.priceMensual?.amount ?? 0);

  readonly fecha = computed(() => this.cotizacionData()?.data.plan.effectiveDate || '0000-00-00');

  readonly valorAPagar = computed(() => {
    const plan = this.pagoPlanForChild();
    if (!plan) return 0;
    return this.paymentFrequencyText() === 'Mensual'
      ? (plan.priceMensual?.amount ?? 0)
      : (plan.priceAnual?.amount ?? 0);
  });

  readonly assetForChild = computed<Asset | null>(() => {
    const asset = this.cotizacionData()?.data.asset;
    if (!asset) return null;
    return asset;
  });

  readonly vehicleDescription = computed<string>(() => {
    const asset = this.assetForChild();
    if (!asset) return '';
    return `${asset.brand} ${asset.model} ${asset.cylinder}`;
  });

  readonly ownerFullName = computed<string>(() => {
    const asset = this.assetForChild();
    if (!asset) return '';
    return asset.owner.fullname || '';
  });

  readonly ownerDocument = computed<string>(() => {
    const asset = this.assetForChild();
    if (!asset || !asset.owner.document) return '';
    return asset.owner.document.number || '';
  });

  ngOnInit(): void {
    this.initializeComponent();
  }

  // ===================================================================
  // MÉTODOS PÚBLICOS Y MANEJADORES DE EVENTOS
  // ===================================================================

  entendido(): void {
    this.pushDataLayer('Button', 'Entendido');
    window.location.href = 'https://www.sura.co/';
  }
  // ===================================================================
  // MÉTODOS PRIVADOS Y LÓGICA DE INICIALIZACIÓN
  // ===================================================================

  private initializeComponent(): void {
    this.route.queryParams.subscribe((params) => {
      const prepolicyid = params['prepolicyid'];
      this.codAsesor.set(params['codigoAsesor'] || '');

      if (!prepolicyid) {
        console.error('No se encontró el "prepolicyid" en la URL.');
        this.isLoading.set(false);
        return;
      }

      this.checkPaymentStatus(prepolicyid);

      this.cotizacionService.getCotizacion(this.sessionId(), prepolicyid).subscribe({
        next: (data) => {
          if (!data) {
            console.error(
              'Error al obtener la cotización desde la URL:',
              'La data no trae información',
            );
            this.utils.handleredirect(this.codAsesor(), REDIRECT);
            return;
          }
          this.cotizacionData.set(data);
          this.pushDataLayer('Pageview');
          this.isLoading.set(false);
        },
        error: (error) => {
          console.error('Error al obtener la cotización desde la URL:', error);
          this.utils.handleredirect(this.codAsesor(), REDIRECT);
          this.isLoading.set(false);
        },
      });
    });
  }

  private checkPaymentStatus(recordId: string): void {
    this.membershipService.getTokenResponse(this.sessionId(), recordId).subscribe({
      next: (response: MembershipTokenResponse) => {
        if (response.data.status === 'APPROVED') {
          this.router.navigate([PAGO_APROBADO_NO_AP], {
            queryParams: { prepolicyid: recordId },
          });
        } else if (response.data.status === 'FAILED' || response.data.status === 'REJECTED') {
          this.router.navigate([PAGO_RECHAZADO], {
            queryParams: { prepolicyid: recordId },
          });
        }
      },
      error: (err) => {
        console.error('Error al verificar el estado del pago, redirigiendo a rechazado:', err);
      },
    });
  }

  private pushDataLayer(ecom_item: 'Pageview' | 'Button', ecom_subitem = ''): void {
    const cotizacion = this.cotizacionData();
    if (!cotizacion) return; // No enviar datos si no hay cotización

    // Adaptamos los datos para los helpers
    const vehicleDataForHelper = {
      LeadResponse: { data: { vehicle: cotizacion.data.asset } },
    };
    const planesDataForHelper = {
      payment_frequency: cotizacion.data.plan.paymentFrecuency,
      selected_plan: cotizacion.data.plan.selected,
    };

    // this.dataLayerService.pushDataLayer({
    //   event: 'moto_ecommerce_step_6_procesando_pago',
    //   ecom_tipo: 'Moto',
    //   ecom_item,
    //   ecom_subitem,
    //   ecom_pantalla: 'Procesando Pago',
    //   user_id: this.sessionId(),
    //   ...getVehicleData(vehicleDataForHelper as any),
    //   ...getPlanesData(planesDataForHelper as any),
    //   dueño_paga_seguro: 'Si',
    // });
  }
}
